import { useState, useMemo, useEffect, useCallback } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import {
  Container,
  Pagination,
  Breadcrumb,
  Modal,
  Button,
} from "components";
import {
  FilterWithDateForm,
  ContentListItem,
  ListPageTitle,
} from "common";
import {
  useBlogs,
  useBlogsCategories,
} from "./hooks";

import { BlogModalContent } from "./BlogModalContent";
import { generateImageLink } from "utils/generateImageLinks";

import {
  ENUM_FilterDateRangeActionType,
} from "reducers/filterDateRange";
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import "./Blog.scss";

import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { languages_enum } from "redux/actions/globalSlice";

export interface showBlogModalProps {
  show: boolean;
  value: number;
  index: number;
}

export function Blog() {
  const { blogsCategories, isLoadingCategories } = useBlogsCategories();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { page } = useParams();
  const location = useLocation();

  const language = useSelector((state: RootState) => state.global.language);
  const prefix = language === languages_enum.GEORGIAN ? "" : `/${language}`;

  const getQueryParams = (param: string) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(param);
  };

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [category_id, setCategory_id] = useState<string | number>(0);
  const searchKeyword = getQueryParams("q") || "";

  const {
    blogs,
    PAGE_SIZE,
    maxPages,
    filterDates,
    setFiltersDateRange,
  } = useBlogs({ currentPage, category_id, search: searchKeyword });

  const [pressedNext, setPressedNext] = useState(false);
  const [pressedPrev, setPressedPrev] = useState(false);

  const asyncShowNextPageModal = async () => {
    try {
      const result = await blogs;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/blog/${currentPage}/${result[0]?.id}/${result[0]?.slug}`
        );
        setPressedNext(false);
      }
    } catch (error) {
      console.error("Error navigating to blog:", error);
    }
  };

  const asyncShowPrevPageModal = async () => {
    try {
      const result = await blogs;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/blog/${currentPage}/${result[result.length - 1]?.id}/${result[result.length - 1]?.slug}`
        );
        setPressedPrev(false);
      }
    } catch (error) {
      console.error("Error navigating to blog:", error);
    }
  };

  useEffect(() => {
    if (pressedNext) asyncShowNextPageModal();
    if (pressedPrev) asyncShowPrevPageModal();
  }, [pressedNext, currentPage, blogs, pressedPrev]);

  const [currentBlogId, setCurrentBlogId] = useState<string | number>();

  const currentBlogIndex = useMemo(() => {
    return blogs?.findIndex((blog: any) =>
      String(blog?.id) === String(currentBlogId)
    );
  }, [blogs, currentBlogId]);

  useEffect(() => {
    if (Number(page) <= maxPages && !pressedNext && !pressedPrev) {
      setCurrentPage(Number(page));
    }
    const categoryFromUrl = getQueryParams("category");
    if (categoryFromUrl) {
      setCategory_id(Number(categoryFromUrl));
    }
  }, [page, maxPages, location.search]);

  const navigateCurrentBlog = useCallback(
    (path: string) => {
      navigate(`${prefix}/blog/${currentPage}/${path}`);
    },
    [currentPage, prefix]
  );

  const updateUrlWithCategory = useCallback(
    (newCategoryId: number) => {
      const url = newCategoryId
        ? `${prefix}/blog?category=${newCategoryId}`
        : `${prefix}/blog/${currentPage}`;
      setCurrentPage(1);
      navigate(url, { replace: true });
    },
    [currentPage, navigate, prefix]
  );

  const handleCategoryClick = useCallback(
    (id: number) => {
      setCategory_id(id);
      updateUrlWithCategory(id);
    },
    [updateUrlWithCategory]
  );

  return (
    <>
      <Container className="blog">
        <header className="blog-header">
          <ListPageTitle>
            {formatMessage({
              id: "blog.title",
              defaultMessage: "_blog_",
            })}
          </ListPageTitle>
          <Breadcrumb
            links={[
              {
                label: formatMessage({
                  id: "main.breadcrumb",
                  defaultMessage: "_main_",
                }),
                value: `${prefix}/`,
              },
              {
                label: formatMessage({
                  id: "blog.breadcrumb",
                  defaultMessage: "_blog_",
                }),
                value: `${prefix}/blog/${currentPage}`,
              },
            ]}
          />

          <div className="blog-filter-container">
            <select
              style={{
                float: "left",
                borderRadius: "24px",
                border: "solid 1px #d2d2d2",
                color: "#303134",
                fontWeight: 500,
                fontFamily: "Helvetica Medium",
                fontSize: "1rem",
                outline: "none",
                maxWidth: "95%",
                padding: "0.875rem 1.0625rem 0.875rem 0.625rem",
              }}
              value={category_id}
              onChange={(e) => handleCategoryClick(Number(e.target.value))}
              className="blog-filter-container__select"
            >
              <option value={0}>ყველა</option>
              {!isLoadingCategories &&
                blogsCategories?.status === 200 &&
                blogsCategories.data.map((category: any) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
            </select>
<input
  type="text"
  value={getQueryParams("q") || ""}
  onChange={(e) => {
    const query = e.target.value;
    const searchParam = query ? `&q=${encodeURIComponent(query)}` : "";
    navigate(`${prefix}/blog/1?category=${category_id}${searchParam}`, {
      replace: true,
    });
  }}
  placeholder={formatMessage({
    id: "blog.search",
    defaultMessage: "Search...",
  })}
  style={{
    borderRadius: "24px",
    border: "solid 1px #d2d2d2",
    padding: "0.875rem 1rem",
    fontSize: "1rem",
    marginRight: "1rem",
    maxWidth: "100%",
    marginLeft: "auto",
  }}
/>
            <FilterWithDateForm
              placeholderText={formatMessage({
                id: "blog.date",
                defaultMessage: "_date_",
              })}
          
              startDate={filterDates[0]}
              endDate={filterDates[1]}
              onClickFilter={() => {
                setFiltersDateRange({
                  type: ENUM_FilterDateRangeActionType.FILTERDATES,
                  payload: filterDates,
                });
              }}
              onChange={(e: any) =>
                setFiltersDateRange({
                  type: ENUM_FilterDateRangeActionType.UPDATEDATES,
                  payload: e,
                })
              }
            />
          </div>
        </header>

        <div className="blog-content">
          {blogs?.map((blog: any) => (
            <ContentListItem
              key={blog?.id}
              title={blog?.title}
              subtitle={moment(blog?.created_at).format("DD/MM/YYYY")}
              category_id={blog?.category_id}
              image={generateImageLink({ imageId: blog.cover_image })}
              link={`${prefix}/blog/${currentPage}/${blog?.id}/${blog?.slug}`}
            />
          ))}
        </div>

        {!!maxPages && (
          <Pagination
            className="blog-pagination"
            maxPages={maxPages}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            onChange={(newPage: number) =>
    navigate(
      `${prefix}/blog/${newPage}?category=${category_id}` +
      (searchKeyword ? `&q=${encodeURIComponent(searchKeyword)}` : "")
    )
  }
          />
        )}
      </Container>

      <Routes>
        <Route
          path=":id/:slug"
          element={
            <Modal
              show={true}
              onClose={() => navigate(`${prefix}/blog/${currentPage}`)}
              next={() => {
                if (currentBlogId == blogs[blogs.length - 1]?.id) {
                  if (currentPage !== maxPages) {
                    setCurrentPage(currentPage + 1);
                    setPressedNext(true);
                  }
                } else if (currentBlogIndex !== -1) {
                  navigateCurrentBlog(
                    `${blogs[currentBlogIndex + 1]?.id}/${blogs[currentBlogIndex + 1]?.slug}`
                  );
                }
              }}
              prev={() => {
                if (currentBlogId == blogs[0]?.id) {
                  if (currentPage !== 1) {
                    setCurrentPage(currentPage - 1);
                    setPressedPrev(true);
                  }
                } else if (currentBlogIndex !== -1) {
                  navigateCurrentBlog(
                    `${blogs[currentBlogIndex - 1]?.id}/${blogs[currentBlogIndex - 1]?.slug}`
                  );
                }
              }}
            >
              <BlogModalContent setCurrentBlogId={setCurrentBlogId} />
            </Modal>
          }
        />
      </Routes>
    </>
  );
}
